import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Case, Comment, CaseResolutionData } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useEmailVerificationGuard } from '../hooks/useEmailVerification';
import { getRoleDisplay, getRoleBadgeClass, getGenderedRoleLabel, canResolveCases, isModerator, isAdmin } from '../utils/roles';
import { getMedicalRoleLabel } from '../utils/medicalRoles';
import { CaseResolutionModal } from './CaseResolutionModal';
import { CaseStatusBanner } from './CaseStatusBanner';
import { PathologyImageViewer } from './PathologyImageViewer';
import { BasicImageViewer } from './BasicImageViewer';
import { getViewerType, hasAdvancedViewerAccess, getUpgradeMessage } from '../utils/viewerSelection';

interface CommentItemProps {
  comment: Comment;
  depth: number;
  onReply: (commentId: string, authorName: string) => void;
  onVote: (commentId: string) => void;
  currentUserId?: string;
  currentUserRole?: string;
}

function CommentItem({ comment, depth, onReply, onVote, currentUserId, currentUserRole }: CommentItemProps) {
  const [showReplies, setShowReplies] = useState(true);
  const isOwner = currentUserId === comment.userId;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Πριν από λίγο';
    if (diffInHours < 24) return `Πριν από ${diffInHours} ώρες`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Χθες';
    if (diffInDays < 7) return `Πριν από ${diffInDays} μέρες`;
    return date.toLocaleDateString('el-GR');
  };

  return (
    <div className={`${depth > 0 ? 'ml-8 border-l border-gray-200 pl-4' : ''}`}>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        {/* Comment Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {comment.author?.firstName?.charAt(0)}{comment.author?.lastName?.charAt(0)}
            </div>
            <div>
              <p className="font-medium text-gray-900">
                Δρ. {comment.author?.firstName} {comment.author?.lastName}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {comment.author?.role && (
                  <span className={getRoleBadgeClass(comment.author.role)}>
                    {getGenderedRoleLabel(comment.author.role, comment.author.sex)}
                  </span>
                )}
                {comment.author?.medicalRole && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {getMedicalRoleLabel(comment.author.medicalRole, comment.author.sex)}
                  </span>
                )}
                {comment.author?.specialty && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {comment.author.specialty}
                  </span>
                )}
              </div>
              {comment.author?.hospital && (
                <p className="text-xs text-gray-500 mt-1">
                  {comment.author.hospital}
                </p>
              )}
              <p className="text-sm text-gray-500">
                {formatDate(comment.createdAt.toString())}
                {comment.isEdited && ' (επεξεργασμένο)'}
              </p>
            </div>
          </div>
          {isOwner && (
            <div className="flex items-center space-x-2">
              <button className="text-gray-400 hover:text-gray-600 text-sm">
                Επεξεργασία
              </button>
            </div>
          )}
        </div>

        {/* Comment Content */}
        <div className="prose prose-sm max-w-none mb-4">
          <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
        </div>

        {/* Comment Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onVote(comment.id)}
                className={`transition-all duration-200 ${
                  comment.userVote === 'upvote' 
                    ? 'p-2 rounded-lg bg-green-100 text-green-700 font-bold border-2 border-green-300 shadow-lg' 
                    : 'p-1 text-gray-500 hover:text-green-600 hover:scale-105'
                }`}
                style={comment.userVote === 'upvote' ? { 
                  transform: 'scale(1.1)', 
                  fontWeight: 'bold' 
                } : {}}
              >
                <span style={{ fontSize: '18px' }}>👍</span>
              </button>
              <span className={`text-sm ml-1 ${
                comment.userVote === 'upvote' ? 'text-green-700 font-bold' : 'text-gray-600'
              }`}>
                {comment.voteCount || 0}
              </span>
            </div>

            <button
              onClick={() => onReply(comment.id, `Δρ. ${comment.author?.firstName} ${comment.author?.lastName}`)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Απάντηση
            </button>

            {comment.replies && comment.replies.length > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                {showReplies ? 'Απόκρυψη' : 'Εμφάνιση'} {comment.replies.length} απαντήσεων
              </button>
            )}
          </div>
          
          {/* Moderation actions */}
          <div className="flex items-center space-x-2">
            {currentUserId && currentUserRole && (isModerator(currentUserRole as any) || isAdmin(currentUserRole as any)) && (
              <button className="text-xs text-red-600 hover:text-red-800">
                Διαγραφή
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      {showReplies && comment.replies && comment.replies.length > 0 && (
        <div className="space-y-2">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              onReply={onReply}
              onVote={onVote}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CaseDetailView() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkEmailVerification } = useEmailVerificationGuard();
  
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageViewerIndex, setImageViewerIndex] = useState<number | null>(null);
  const [commentText, setCommentText] = useState('');
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [replyToCommentAuthor, setReplyToCommentAuthor] = useState<string | null>(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentFormRef = useRef<HTMLDivElement>(null);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionLoading, setResolutionLoading] = useState(false);
  
  // Viewer selection logic
  const viewerType = getViewerType(user);
  const upgradeMessage = getUpgradeMessage(user);

  useEffect(() => {
    fetchCaseDetails();
    fetchComments();
  }, [caseId]);

  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      console.log('Fetching case details for:', caseId);
      const apiUrl = `/api/v1/cases/${caseId}`;
      console.log('API URL:', apiUrl);
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      console.log('Case fetch response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Case fetch failed:', errorData);
        throw new Error(errorData?.message || 'Σφάλμα κατά τη φόρτωση του περιστατικού');
      }

      const data = await response.json();
      console.log('Case data received:', data);
      console.log('Case data structure:', data.data);
      console.log('Case object:', data.data?.case);
      setCaseData(data.data.case);

      // View count is automatically incremented when fetching case details

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const handleReplyClick = (commentId: string, authorName: string) => {
    setReplyToCommentId(commentId);
    setReplyToCommentAuthor(authorName);
    // Scroll to comment form with smooth animation
    setTimeout(() => {
      commentFormRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      // Focus on the textarea
      const textarea = commentFormRef.current?.querySelector('textarea');
      if (textarea) {
        (textarea as HTMLTextAreaElement).focus();
      }
    }, 100);
  };

  const fetchComments = async () => {
    try {
      const headers: HeadersInit = {};
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api/comments/case/${caseId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setComments(data.data.comments);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || !user) return;

    setSubmittingComment(true);
    try {
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          caseId,
          content: commentText.trim(),
          parentCommentId: replyToCommentId
        })
      });

      if (response.ok) {
        setCommentText('');
        setReplyToCommentId(null);
        setReplyToCommentAuthor(null);
        fetchComments(); // Refresh comments
      } else {
        throw new Error('Σφάλμα κατά την αποστολή του σχολίου');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Σφάλμα αποστολής σχολίου');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleVote = async (commentId: string) => {
    if (!user) return;
    
    // Check email verification before allowing voting
    if (!checkEmailVerification('ψηφίσετε σε σχόλια')) {
      return;
    }

    try {
      const response = await fetch(`/api/comments/${commentId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({ voteType: 'upvote' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Vote failed:', response.status, errorData);
        return;
      }

      fetchComments(); // Refresh to get updated vote counts
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const handleResolveCase = async (resolutionData: CaseResolutionData) => {
    if (!caseData || !user) return;

    setResolutionLoading(true);
    try {
      const response = await fetch(`/api/v1/cases/${caseData.id}/resolve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          final_diagnosis: resolutionData.finalDiagnosis,
          resolution_notes: resolutionData.resolutionNotes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Αποτυχία επίλυσης περιστατικού');
      }

      // Refresh case data to show resolved status
      await fetchCaseDetails();
      setShowResolutionModal(false);
      
      // Show success message (you could use a toast library here)
      alert('Το περιστατικό επιλύθηκε επιτυχώς!');
      
    } catch (err) {
      console.error('Error resolving case:', err);
      throw err; // Re-throw to let the modal handle the error
    } finally {
      setResolutionLoading(false);
    }
  };

  const handleReopenCase = async () => {
    if (!caseData || !user) return;

    const confirmed = window.confirm(
      'Είστε σίγουροι ότι θέλετε να επαναφέρετε αυτό το περιστατικό; Θα γίνει πάλι ανοικτό για συζήτηση.'
    );

    if (!confirmed) return;

    setResolutionLoading(true);
    try {
      const response = await fetch(`/api/v1/cases/${caseData.id}/reopen`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Αποτυχία επαναφοράς περιστατικού');
      }

      // Refresh case data to show open status
      await fetchCaseDetails();
      alert('Το περιστατικό επαναφέρθηκε επιτυχώς!');
      
    } catch (err) {
      console.error('Error reopening case:', err);
      alert(err instanceof Error ? err.message : 'Αποτυχία επαναφοράς περιστατικού');
    } finally {
      setResolutionLoading(false);
    }
  };

  const handleDeleteCase = async () => {
    if (!caseData || !user) return;

    const confirmed = window.confirm(
      'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το περιστατικό; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.'
    );

    if (!confirmed) return;

    setResolutionLoading(true);
    try {
      const response = await fetch(`/api/v1/cases/${caseData.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Αποτυχία διαγραφής περιστατικού');
      }

      // Navigate back to cases list
      navigate('/cases');
      
      // Show success message
      alert('Το περιστατικό διαγράφηκε επιτυχώς!');
      
    } catch (err) {
      console.error('Error deleting case:', err);
      alert('Σφάλμα κατά τη διαγραφή του περιστατικού: ' + (err instanceof Error ? err.message : 'Άγνωστο σφάλμα'));
    } finally {
      setResolutionLoading(false);
    }
  };

  const handleArchiveCase = async () => {
    if (!caseData || !user) return;

    const confirmed = window.confirm(
      'Είστε σίγουροι ότι θέλετε να αρχειοθετήσετε αυτό το περιστατικό; Θα είναι ορατό σε όλους τους συνδεδεμένους χρήστες στο αρχείο.'
    );

    if (!confirmed) return;

    setResolutionLoading(true);
    try {
      const response = await fetch(`/api/v1/cases/${caseData.id}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Αποτυχία αρχειοθέτησης περιστατικού');
      }

      // Refresh case data to show archived status
      await fetchCaseDetails();
      alert('Το περιστατικό αρχειοθετήθηκε επιτυχώς!');
      
    } catch (err) {
      console.error('Error archiving case:', err);
      alert(err instanceof Error ? err.message : 'Αποτυχία αρχειοθέτησης περιστατικού');
    } finally {
      setResolutionLoading(false);
    }
  };

  const openImageViewer = (index: number) => {
    setImageViewerIndex(index);
  };

  const closeImageViewer = () => {
    setImageViewerIndex(null);
  };

  const nextImage = () => {
    if (caseData?.images && imageViewerIndex !== null) {
      setImageViewerIndex((imageViewerIndex + 1) % caseData.images.length);
    }
  };

  const previousImage = () => {
    if (caseData?.images && imageViewerIndex !== null) {
      setImageViewerIndex(
        imageViewerIndex === 0 ? caseData.images.length - 1 : imageViewerIndex - 1
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUrgencyColor = (urgency: string) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'normal': 'bg-blue-100 text-blue-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    return colors[urgency as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getUrgencyLabel = (urgency: string) => {
    const labels = {
      'low': 'Εκπαιδευτικό',
      'normal': 'Κανονική',
      'high': 'Υψηλή',
      'urgent': 'Επείγον'
    };
    return labels[urgency as keyof typeof labels] || urgency;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Σφάλμα φόρτωσης περιστατικού
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Επιστροφή
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20">
      {/* Header */}
      <div className="mb-6">
        {/* Anonymous submission notification for case owner */}
        {caseData.isAnonymous && user && user.id === caseData.userId && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Ανώνυμη Υποβολή</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Έχετε υποβάλει αυτό το περιστατικό ανώνυμα. Τα στοιχεία σας (όνομα, νοσοκομείο και ρόλος χρήστη) 
                  δεν εμφανίζονται στους άλλους χρήστες, εκτός από τους συντονιστές και διαχειριστές.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Anonymous submission notification for admins/moderators */}
        {caseData.isAnonymous && user && user.id !== caseData.userId && (isModerator(user.role) || isAdmin(user.role)) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Ανώνυμο Περιστατικό</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Αυτό το περιστατικό έχει υποβληθεί ανώνυμα. Ως {isAdmin(user.role) ? 'διαχειριστής' : 'συντονιστής'}, 
                  μπορείτε να δείτε τα πραγματικά στοιχεία του δημιουργού στην πλευρική στήλη.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            ← Επιστροφή
          </button>
          
          {user && (
            <div className="flex space-x-2">
              {/* Owner, Moderator, and Admin actions */}
              {(user.id === caseData.userId || isModerator(user.role) || isAdmin(user.role)) && (
                <>
                  <Link
                    to={`/cases/${caseId}/edit`}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Επεξεργασία
                  </Link>
                  <button 
                    onClick={handleDeleteCase}
                    disabled={resolutionLoading}
                    className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Διαγραφή
                  </button>
                </>
              )}
              
              {/* Resolution actions */}
              {(user.id === caseData.userId || isModerator(user.role) || isAdmin(user.role)) ? (
                <>
                  {caseData.status === 'open' && (
                    <button 
                      onClick={() => setShowResolutionModal(true)}
                      className="px-3 py-1 text-sm text-green-600 border border-green-300 rounded-md hover:bg-green-50 disabled:opacity-50"
                      disabled={resolutionLoading}
                    >
                      {resolutionLoading ? <LoadingSpinner size="sm" /> : 'Επίλυση Περιστατικού'}
                    </button>
                  )}
                  
                  {caseData.status === 'resolved' && (
                    <button 
                      onClick={handleReopenCase}
                      className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50"
                      disabled={resolutionLoading}
                    >
                      {resolutionLoading ? <LoadingSpinner size="sm" /> : 'Επαναφορά Περιστατικού'}
                    </button>
                  )}
                </>
              ) : null}
              
              {/* Moderator and Admin actions */}
              {(isModerator(user.role) || isAdmin(user.role)) && (
                <>
                  {(caseData.status === 'open' || caseData.status === 'resolved') && (
                    <button 
                      onClick={handleArchiveCase}
                      className="px-3 py-1 text-sm text-orange-600 border border-orange-300 rounded-md hover:bg-orange-50 disabled:opacity-50"
                      disabled={resolutionLoading}
                    >
                      {resolutionLoading ? <LoadingSpinner size="sm" /> : 'Αρχειοθέτηση'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex items-center gap-2">
            {caseData.urgencyLevel === 'urgent' && (
              <span className="text-red-600 text-lg animate-pulse" title="Επείγον">
                🚨
              </span>
            )}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getUrgencyColor(caseData.urgencyLevel)}`}>
              {getUrgencyLabel(caseData.urgencyLevel)}
            </span>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            {caseData.caseType}
          </span>
          {caseData.tissueType && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              {caseData.tissueType}
            </span>
          )}
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            caseData.status === 'open' ? 'bg-green-100 text-green-800' :
            caseData.status === 'resolved' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {caseData.status === 'open' && 'Ανοικτό'}
            {caseData.status === 'resolved' && 'Λυμένο'}
            {caseData.status === 'archived' && 'Αρχειοθετημένο'}
          </span>
        </div>

        <div className="flex items-start gap-3 mb-4">
          {caseData.urgencyLevel === 'urgent' && (
            <span className="text-red-600 text-2xl animate-pulse mt-1" title="Επείγον Περιστατικό">
              🚨
            </span>
          )}
          <h1 className={`text-3xl font-bold mb-0 ${
            caseData.urgencyLevel === 'urgent' ? 'text-red-900' : 'text-gray-900'
          }`}>
            {caseData.title}
          </h1>
        </div>
        
        <div className="flex items-center text-sm text-gray-600 space-x-4">
          <span>👁️ {caseData.viewCount || 0} προβολές</span>
          <span>💬 {comments.length} σχόλια</span>
          <span>📅 {formatDate(caseData.createdAt.toString())}</span>
        </div>
      </div>

      {/* Case Status Banner */}
      <CaseStatusBanner case={caseData} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Images */}
          {caseData.images && caseData.images.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Εικόνες ({caseData.images.length})
                </h2>
                
                {/* Viewer capability indicator */}
                <div className="flex items-center space-x-2">
                  {viewerType === 'advanced' ? (
                    <div className="flex items-center space-x-1 text-sm text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      <span className="text-xs">🔬</span>
                      <span className="font-medium">Προηγμένος Slide Viewer</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1 text-sm text-blue-700 bg-blue-50 px-2 py-1 rounded-full">
                      <span className="text-xs">👁️</span>
                      <span className="font-medium">Βασικός Slide Viewer</span>
                    </div>
                  )}
                  
                  {/* Info tooltip */}
                  <div className="group relative">
                    <button className="text-gray-400 hover:text-gray-600 text-sm">
                      ℹ️
                    </button>
                    <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                      {viewerType === 'advanced' ? (
                        <>
                          <div className="font-medium mb-1">Προηγμένος Slide Viewer</div>
                          <div className="space-y-1 text-xs opacity-90">
                            <div>• Zoom έως 40x μεγέθυνση</div>
                            <div>• Πλοήγηση με drag & pan</div>
                            <div>• Mini-map navigator</div>
                            <div>• Επαγγελματικά εργαλεία</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-medium mb-1">Βασικός Slide Viewer</div>
                          <div className="space-y-1 text-xs opacity-90">
                            <div>• Απλή προβολή εικόνων</div>
                            <div>• Πλοήγηση μεταξύ εικόνων</div>
                            <div>• Touch-friendly</div>
                            {upgradeMessage && (
                              <div className="text-yellow-300 mt-2">
                                💡 {upgradeMessage}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {caseData.images.map((image, index) => (
                  <div
                    key={image.id}
                    className="cursor-pointer group relative aspect-square bg-gray-100 rounded-lg overflow-hidden"
                    onClick={() => openImageViewer(index)}
                  >
                    <img
                      src={image.imageUrl}
                      alt={image.caption || `Image ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 text-lg">🔍</span>
                    </div>
                    {image.imageType && (
                      <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                        {image.imageType === 'gross' && 'Μακροσκοπική'}
                        {image.imageType === 'microscopic' && 'Μικροσκοπική'}
                        {image.imageType === 'special_stain' && 'Ειδική Χρώση'}
                        {image.imageType === 'immunohistochemistry' && 'Ανοσοϊστοχημεία'}
                        {image.imageType === 'molecular' && 'Μοριακή'}
                        {image.imageType === 'other' && 'Άλλο'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Case Description */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Περιγραφή Περιστατικού</h2>
            <div className="prose prose-sm max-w-none space-y-4">
              <p className="text-gray-700 whitespace-pre-wrap">{caseData.description}</p>
              
              {(caseData.macroscopicDescription || caseData.macroscopic_description) && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Μακροσκοπική Περιγραφή</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{caseData.macroscopicDescription || caseData.macroscopic_description}</p>
                </div>
              )}
              
              {(caseData.microscopicDescription || caseData.microscopic_description) && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Μικροσκοπική Περιγραφή</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{caseData.microscopicDescription || caseData.microscopic_description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Medical Information */}
          {((caseData.immunohistochemistryResults || caseData.immunohistochemistry_results) || 
            (caseData.specialStains || caseData.special_stains) || 
            ((caseData.finalDiagnosis || caseData.final_diagnosis) && caseData.status === 'resolved')) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Πρόσθετες Ιατρικές Πληροφορίες</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(caseData.immunohistochemistryResults || caseData.immunohistochemistry_results) && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Ανοσοϊστοχημεία</h3>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{caseData.immunohistochemistryResults || caseData.immunohistochemistry_results}</p>
                  </div>
                )}
                
                {(caseData.specialStains || caseData.special_stains) && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Ειδικές Χρώσεις</h3>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{caseData.specialStains || caseData.special_stains}</p>
                  </div>
                )}
                
                {(caseData.finalDiagnosis || caseData.final_diagnosis) && caseData.status === 'resolved' && (
                  <div className="md:col-span-2 bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-medium text-green-900 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Τελική Διάγνωση (Επιλυμένο)
                    </h3>
                    <p className="text-green-800 text-sm whitespace-pre-wrap font-medium">{caseData.finalDiagnosis || caseData.final_diagnosis}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Συζήτηση ({comments.length})
            </h2>

            {/* Add Comment */}
            {user && caseData.status === 'open' ? (
              <div className="mb-6" ref={commentFormRef}>
                {replyToCommentId && replyToCommentAuthor && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                    <span className="text-sm text-blue-700">
                      Απαντάτε στον/στην <strong>{replyToCommentAuthor}</strong>
                    </span>
                    <button
                      onClick={() => {
                        setReplyToCommentId(null);
                        setReplyToCommentAuthor(null);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-lg font-bold"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder={replyToCommentId ? 'Γράψτε την απάντησή σας...' : 'Γράψτε το σχόλιό σας...'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-2">
                        {replyToCommentId && (
                          <button
                            onClick={() => {
                              setReplyToCommentId(null);
                              setReplyToCommentAuthor(null);
                            }}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Ακύρωση απάντησης
                          </button>
                        )}
                      </div>
                      <button
                        onClick={handleCommentSubmit}
                        disabled={!commentText.trim() || submittingComment}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submittingComment ? <LoadingSpinner size="sm" /> : 'Αποστολή'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : caseData.status !== 'open' ? (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-gray-600">
                  {caseData.status === 'resolved' && 'Αυτό το περιστατικό έχει επιλυθεί. Δεν επιτρέπονται νέα σχόλια.'}
                  {caseData.status === 'archived' && 'Αυτό το περιστατικό έχει αρχειοθετηθεί.'}
                </p>
              </div>
            ) : !user ? (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-gray-600 mb-2">Συνδεθείτε για να συμμετάσχετε στη συζήτηση</p>
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Σύνδεση
                </Link>
              </div>
            ) : null}

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">💬</div>
                  <p>Δεν υπάρχουν σχόλια ακόμα</p>
                  <p className="text-sm">Γίνετε ο πρώτος που θα σχολιάσει!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    depth={0}
                    onReply={handleReplyClick}
                    onVote={handleVote}
                    currentUserId={user?.id}
                    currentUserRole={user?.role}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Case Owner */}
          {caseData.user && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Δημιουργός</h3>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                  {caseData.user.firstName?.charAt(0)}{caseData.user.lastName?.charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Δρ. {caseData.user.firstName} {caseData.user.lastName}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {caseData.user.role && (
                      <span className={getRoleBadgeClass(caseData.user.role)}>
                        {getGenderedRoleLabel(caseData.user.role, caseData.user.sex)}
                      </span>
                    )}
                    {caseData.user.medicalRole && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {getMedicalRoleLabel(caseData.user.medicalRole, caseData.user.sex)}
                      </span>
                    )}
                  </div>
                  {caseData.user.specialty && (
                    <p className="text-sm text-gray-600">{caseData.user.specialty}</p>
                  )}
                  {caseData.user.hospitalAffiliation && (
                    <p className="text-sm text-gray-600">{caseData.user.hospitalAffiliation}</p>
                  )}
                  {caseData.user.yearsOfExperience && (
                    <p className="text-xs text-gray-500">{caseData.user.yearsOfExperience} έτη εμπειρίας</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Patient Information */}
          {(caseData.patientAge || caseData.patientGender || caseData.clinicalHistory) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Στοιχεία Ασθενούς</h3>
              <div className="space-y-3">
                {caseData.patientAge && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Ηλικία:</span>
                    <span className="ml-2 text-sm text-gray-900">{caseData.patientAge} ετών</span>
                  </div>
                )}
                {caseData.patientGender && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Φύλο:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {caseData.patientGender === 'male' && 'Άνδρας'}
                      {caseData.patientGender === 'female' && 'Γυναίκα'}
                      {caseData.patientGender === 'other' && 'Άλλο'}
                    </span>
                  </div>
                )}
                {caseData.clinicalHistory && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 block mb-1">Κλινικό Ιστορικό:</span>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{caseData.clinicalHistory}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Technical Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Τεχνικές Λεπτομέρειες</h3>
            <div className="space-y-3 text-sm">
              {caseData.stainingMethod && (
                <div>
                  <span className="font-medium text-gray-600">Χρώση:</span>
                  <span className="ml-2 text-gray-900">{caseData.stainingMethod}</span>
                </div>
              )}
              {caseData.magnification && (
                <div>
                  <span className="font-medium text-gray-600">Μεγέθυνση:</span>
                  <span className="ml-2 text-gray-900">{caseData.magnification}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-600">Δημιουργήθηκε:</span>
                <span className="ml-2 text-gray-900">{formatDate(caseData.createdAt.toString())}</span>
              </div>
              {caseData.updatedAt !== caseData.createdAt && (
                <div>
                  <span className="font-medium text-gray-600">Ενημερώθηκε:</span>
                  <span className="ml-2 text-gray-900">{formatDate(caseData.updatedAt.toString())}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Viewer Modal */}
      {imageViewerIndex !== null && caseData.images && (
        <>
          {viewerType === 'advanced' ? (
            <PathologyImageViewer
              images={caseData.images}
              currentIndex={imageViewerIndex}
              onClose={closeImageViewer}
              onNext={nextImage}
              onPrevious={previousImage}
            />
          ) : (
            <BasicImageViewer
              images={caseData.images}
              currentIndex={imageViewerIndex}
              onClose={closeImageViewer}
              onNext={nextImage}
              onPrevious={previousImage}
            />
          )}

          {/* Upgrade message overlay for basic viewer users */}
          {viewerType === 'basic' && upgradeMessage && (
            <div className="fixed bottom-4 left-4 right-4 z-60 flex justify-center">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg shadow-lg max-w-md mx-auto backdrop-blur-sm border border-white/20">
                <div className="flex items-start space-x-3">
                  <div className="text-xl">✨</div>
                  <div>
                    <div className="text-sm font-medium mb-1">Αναβάθμιση διαθέσιμη</div>
                    <div className="text-xs opacity-90">{upgradeMessage}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Case Resolution Modal */}
      <CaseResolutionModal
        isOpen={showResolutionModal}
        onClose={() => setShowResolutionModal(false)}
        onResolve={handleResolveCase}
        case={caseData}
        isSubmitting={resolutionLoading}
      />
    </div>
  );
}